import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import interventionService from '../../../services/interventionService';
import clientService from '../../../services/clientService';
import localService from '../../../services/localService';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';
import LocalSelectDropdown from '../../common/LocalSelectDropdown';

const InterventionCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    clientId: '',
    localId: '',
    typeIntervention: 1,
    observation: '',
    cloture: false
  });
  const [clients, setClients] = useState([]);
  const [locals, setLocals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState({
    clients: false,
    locals: false
  });
  const [error, setError] = useState('');

  // Fetch clients when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  // Fetch locals when client changes
  useEffect(() => {
    if (isOpen) {
      fetchLocals();
    }
  }, [isOpen, form.clientId]);

  const fetchClients = async () => {
    setLoadingData(prev => ({ ...prev, clients: true }));
    try {
      const result = await clientService.getClients(1000, 1);
      if (result.success && Array.isArray(result.data.list)) {
        setClients(result.data.list);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoadingData(prev => ({ ...prev, clients: false }));
    }
  };

  const fetchLocals = async () => {
    setLoadingData(prev => ({ ...prev, locals: true }));
    try {
      // Filter locals by selected client if client is selected
      const filterObj = {};
      if (form.clientId) {
        filterObj.clientId = form.clientId;
      }
      
      const result = await localService.getLocaux(1000, 1, filterObj);
      if (result.success && Array.isArray(result.data.list)) {
        setLocals(result.data.list);
      } else {
        setLocals([]);
      }
    } catch (error) {
      console.error('Error fetching locals:', error);
      setLocals([]);
    } finally {
      setLoadingData(prev => ({ ...prev, locals: false }));
    }
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle client change - reset local selection when client changes
  const handleClientChange = (clientId) => {
    setForm(f => ({
      ...f,
      clientId: clientId,
      localId: '' // Reset local selection when client changes
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    console.log('Submitting form:', form);
    try {
      const payload = {
        ...form,
        clientId: Number(form.clientId),
        localId: form.localId ? Number(form.localId) : null,
        typeIntervention: form.typeIntervention,
      };
      const res = await interventionService.createIntervention(payload);
      if (res.success) {
        onSuccess && onSuccess();
        onClose();
        resetForm();
      } else {
        setError(res.message);
      }
    } catch {
      setError('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      startDate: '',
      endDate: '',
      clientId: '',
      localId: '',
      typeIntervention: 1,
      observation: '',
      cloture: false
    });
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`${isDarkMode ? 'bg-white bg-opacity-10' : 'bg-white bg-opacity-20'} w-10 h-10 rounded-xl flex items-center justify-center mr-3`}>
                <i className="fas fa-plus text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Nouvelle intervention</h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-primary-100'} text-sm`}>Ajouter une intervention</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {error && (
              <div className={`mb-6 p-4 rounded-xl border-l-4 border-l-red-500 ${
                isDarkMode 
                  ? 'bg-red-900/50 text-red-300' 
                  : 'bg-red-50 text-red-700'
              }`}>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                  <div>
                    <div className="font-medium">Erreur</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date Information */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <i className="fas fa-calendar text-blue-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Période d'intervention</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-play mr-2 text-gray-400"></i>
                      Date début *
                    </label>
                    <input
                      type="datetime-local"
                      name="startDate"
                      value={form.startDate}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-600 text-white focus:ring-dark-primary-500' 
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-stop mr-2 text-gray-400"></i>
                      Date fin <span className={`ml-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(facultatif)</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="endDate"
                      value={form.endDate}
                      onChange={handleChange}
                      aria-required={false}
                      required={false}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-600 text-white focus:ring-dark-primary-500' 
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                    />
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Laisser vide si la date de fin n'est pas connue</p>
                  </div>
                </div>
              </div>

              {/* Client and Location */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-amber-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg mr-3">
                    <i className="fas fa-map-marker-alt text-amber-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Client et localisation</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-user mr-2 text-gray-400"></i>
                      Client *
                    </label>
                    <ClientSelectDropdown
                      value={form.clientId}
                      onChange={handleClientChange}
                      options={clients}
                      loading={loadingData.clients}
                      disabled={loadingData.clients}
                      isDarkMode={isDarkMode}
                      required={true}
                      placeholder="Sélectionner un client"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-building mr-2 text-gray-400"></i>
                      Local *
                      {form.clientId && (
                        <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          (filtré par client)
                        </span>
                      )}
                    </label>
                    <LocalSelectDropdown
                      value={form.localId}
                      onChange={val => setForm(f => ({ ...f, localId: val }))}
                      options={locals}
                      loading={loadingData.locals}
                      disabled={loadingData.locals}
                      isDarkMode={isDarkMode}
                      required={true}
                      placeholder={
                        form.clientId 
                          ? "Sélectionner un local "
                          : "Sélectionner d'abord un client"
                      }
                    />
                    {form.clientId && locals.length === 0 && !loadingData.locals && (
                      <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <i className="fas fa-info-circle mr-1"></i>
                        Aucun local trouvé pour ce client
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Intervention Details */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <i className="fas fa-cogs text-green-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Détails de l'intervention</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-tools mr-2 text-gray-400"></i>
                      Type d'intervention *
                    </label>
                    <select
                      name="typeIntervention"
                      value={form.typeIntervention}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-600 text-white focus:ring-dark-primary-500' 
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                    >
                      <option value={1}>Préventive</option>
                      <option value={2}>Curative</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center h-full pt-6">
                    <div className={`flex items-center p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                      <input
                        type="checkbox"
                        name="cloture"
                        checked={form.cloture}
                        onChange={handleChange}
                        className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                      />
                      <label className={`text-sm font-medium flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <i className="fas fa-check-circle mr-2 text-gray-400"></i>
                        Intervention clôturée
                      </label>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-sticky-note mr-2 text-gray-400"></i>
                    Observations
                  </label>
                  <textarea
                    name="observation"
                    value={form.observation}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors resize-none ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                    }`}
                    placeholder="Décrivez les détails de l'intervention, les actions effectuées, les remarques..."
                  />
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 flex-shrink-0 flex justify-end gap-3 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <button
            type="button"
            onClick={handleClose}
            className={`px-6 py-3 rounded-lg transition-colors font-medium border ${
              isDarkMode 
                ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-times mr-2"></i>
            Annuler
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !form.startDate  || !form.clientId}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              loading || !form.startDate|| !form.clientId
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Création...
              </div>
            ) : (
              <div className="flex items-center">
                <i className="fas fa-save mr-2"></i>
                Créer l'intervention
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterventionCreateModal;